import { useState, useCallback, useEffect, useRef } from "react";
import moment from "moment";
import { useNavigate } from "react-router-dom";

import Box from "@mui/material/Box";
import List from "@mui/material/List";
import Badge from "@mui/material/Badge";
import Button from "@mui/material/Button";
import Menu from "@mui/material/Menu";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import ListItemText from "@mui/material/ListItemText";
import ListItemButton from "@mui/material/ListItemButton";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import Tooltip from "@mui/material/Tooltip";
import FlashOnIcon from "@mui/icons-material/FlashOn";
import api from "../../../api";
import Endpoints from "../../../api/Endpoints";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import { noNotifImg } from "../../../assets/images";
import { getUnreadIntelCount, getFeedItems, markAllIntelRead, URGENCY_CONFIG } from "../../../modules/LiveIntelligence/liveIntelData";

import { useSelector } from "react-redux";
import { mono } from "../../../Theme/fonts";

export default function NotificationMenu({ sx, ...other }) {
  const timeRef = useRef(null);
  const { noti_refresh } = useSelector((state) => state.ui);
  const { isLoggedIn } = useSelector((state) => state.auth);
  const [notifications, setNotifications] = useState([]);
  const [totalUnRead, setTotalUnRead] = useState(0);
  const [markLoading, setMarkLoading] = useState(false);
  const navigate = useNavigate();
  const [openMenu, setOpenMenu] = useState(null);

  // ── Live Intel unread state ─────────────────────────────────────────────────
  const [intelUnread, setIntelUnread] = useState(() => getUnreadIntelCount());
  const [intelItems, setIntelItems] = useState([]);

  const loadIntelItems = useCallback(() => {
    const all = getFeedItems();
    const unreadIds = new Set(JSON.parse(localStorage.getItem("ctb_live_intel_unread_ids") || "[]"));
    const unread = all.filter(i => unreadIds.has(i.feed_id)).slice(0, 5);
    setIntelItems(unread);
  }, []);

  const handleOpenMenu = useCallback((event) => {
    getNotifications();
    loadIntelItems();
    setOpenMenu(event.currentTarget);
  }, [loadIntelItems]);

  const handleCloseMenu = useCallback(() => {
    setOpenMenu(null);
  }, []);

  const handleNotiClick = (notification) => {
    handleCloseMenu();
    timeRef.current = setTimeout(
      () => navigate(`/notifications/view/${notification.id}`),
      100
    );
  };

  const handleViewAll = () => {
    handleCloseMenu();
    timeRef.current = setTimeout(() => navigate(`/notifications`), 100);
  };

  const getNotifications = async () => {
    setMarkLoading(true);
    try {
      const res = await api.get(`${Endpoints.notiList}`);
      setNotifications(res?.data);
    } catch (e) {
      console.error("Error fetching notifications", e);
    } finally {
      setMarkLoading(false);
    }
  };

  const getTotalCount = async () => {
    try {
      const res = await api.get(`${Endpoints.notiCount}`);
      setTotalUnRead(res?.data?.unreadCount);
    } catch (e) {
      console.error("Error fetching unread count", e);
    }
  };

  // handle mark all as read
  const handleMarkAllRead = async () => {
    try {
      setMarkLoading(true);
      const res = await api.get(`${Endpoints.markAllRead}`);
      if (res?.code === 200) {
        setTotalUnRead(0);
        setNotifications([]);
      }
    } catch (e) {
      console.error("Error marking all as read", e);
    } finally {
      setMarkLoading(false);
    }
  };

  useEffect(() => {
    if (!isLoggedIn) return;
    getTotalCount();
    return clearTimeout(timeRef.current);
  }, [isLoggedIn, noti_refresh]);

  // Listen for new intel items and for the user marking them read
  useEffect(() => {
    const onUpdate = () => setIntelUnread(getUnreadIntelCount());
    const onRead   = () => { setIntelUnread(0); setIntelItems([]); };
    window.addEventListener("intel-feed-update", onUpdate);
    window.addEventListener("intel-feed-read",   onRead);
    return () => {
      window.removeEventListener("intel-feed-update", onUpdate);
      window.removeEventListener("intel-feed-read",   onRead);
    };
  }, []);


  return (
    <>
      <IconButton
        onClick={handleOpenMenu}
        sx={{
          color: "#64748b",
          width: "36px",
          height: "36px",
          borderRadius: "8px",
          transition: "all 0.2s ease",
          "&:hover": {
            backgroundColor: "#f4f6f9",
            color: "#0085E5",
          },
          ...sx,
        }}
        {...other}
      >
        <Badge
          badgeContent={(totalUnRead || 0) + (intelUnread || 0)}
          color="error"
          sx={{
            "& .MuiBadge-badge": {
              fontSize: "9px",
              fontFamily: mono,
              fontWeight: 700,
              minWidth: "16px",
              height: "16px",
              borderRadius: "4px",
              boxShadow: "0 2px 6px rgba(220,38,38,0.3)",
            },
          }}
        >
          <NotificationsNoneIcon sx={{ fontSize: 20 }} />
        </Badge>
      </IconButton>

      <Menu
        open={!!openMenu}
        anchorEl={openMenu}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            width: 380,
            mt: 1,
            backgroundColor: "#ffffff",
            borderRadius: "10px",
            border: "1px solid #e8eaef",
            boxShadow: "0 8px 30px rgba(0,0,0,0.08), 0 2px 8px rgba(0,0,0,0.04)",
            overflow: "hidden",
            "& .MuiAvatar-root": {
              width: 32,
              height: 32,
              ml: -0.5,
              mr: 1,
            },
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: "20px",
            py: "14px",
            backgroundColor: "#f9fafb",
            borderBottom: "1px solid #f0f1f3",
          }}
        >
          <Box>
            <Typography
              sx={{
                fontFamily: mono,
                fontSize: "10px",
                fontWeight: 700,
                color: "#1e293b",
                letterSpacing: "1.5px",
                textTransform: "uppercase",
              }}
            >
              Notifications
            </Typography>
            <Typography
              sx={{
                fontSize: "12px",
                color: "#94a3b8",
                mt: "2px",
              }}
            >
              {totalUnRead > 0
                ? `${totalUnRead} unread message${totalUnRead > 1 ? "s" : ""}`
                : "All caught up"}
            </Typography>
          </Box>
          {notifications?.length > 0 && (
            <Tooltip disabled={markLoading} title="Mark all as read">
              <IconButton
                onClick={handleMarkAllRead}
                sx={{
                  width: "32px",
                  height: "32px",
                  borderRadius: "6px",
                  color: "#64748b",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor: "#e0f2fe",
                    color: "#0085E5",
                  },
                }}
              >
                <DoneAllIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        {/* Notification list */}
        <Box sx={{ maxHeight: 280, overflowY: "auto" }}>
          {notifications.length > 0 ? (
            <List disablePadding>
              {notifications.map((notification) => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  handleNotiClick={handleNotiClick}
                />
              ))}
            </List>
          ) : (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                py: "2.5rem",
                gap: "8px",
              }}
            >
              <Box
                component="img"
                src={noNotifImg}
                sx={{ height: "120px", width: "120px", opacity: 0.6 }}
              />
              <Typography
                sx={{
                  fontFamily: mono,
                  fontSize: "10px",
                  color: "#94a3b8",
                  letterSpacing: "1px",
                  textTransform: "uppercase",
                }}
              >
                No notifications
              </Typography>
            </Box>
          )}
        </Box>

        {/* ── Live Intel Alerts section ──────────────────────────────────── */}
        {intelItems.length > 0 && (
          <>
            {/* Section header */}
            <Box
              sx={{
                px: "20px",
                py: "10px",
                backgroundColor: "#fef2f2",
                borderTop: "1px solid #fecaca",
                borderBottom: "1px solid #fecaca",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.7 }}>
                <FlashOnIcon sx={{ fontSize: 12, color: "#dc2626" }} />
                <Typography
                  sx={{
                    fontFamily: mono,
                    fontSize: "9px",
                    fontWeight: 700,
                    color: "#dc2626",
                    letterSpacing: "1.5px",
                    textTransform: "uppercase",
                  }}
                >
                  Live Intel Alerts
                </Typography>
                <Box
                  sx={{
                    ml: 0.5,
                    px: "5px",
                    py: "1px",
                    borderRadius: "3px",
                    backgroundColor: "#dc2626",
                    color: "#fff",
                    fontFamily: mono,
                    fontSize: "8px",
                    fontWeight: 700,
                  }}
                >
                  {intelItems.length}
                </Box>
              </Box>
              <Button
                size="small"
                onClick={() => {
                  markAllIntelRead();
                  setIntelUnread(0);
                  setIntelItems([]);
                }}
                sx={{
                  fontFamily: mono,
                  fontSize: "8px",
                  color: "#dc2626",
                  p: 0,
                  minWidth: 0,
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  "&:hover": { background: "transparent", opacity: 0.7 },
                }}
              >
                Clear
              </Button>
            </Box>

            {/* Intel items */}
            <List disablePadding>
              {intelItems.map((item) => {
                const urgencyCfg = URGENCY_CONFIG[item.urgency] || URGENCY_CONFIG.ROUTINE;
                return (
                  <ListItemButton
                    key={item.feed_id}
                    onClick={() => {
                      handleCloseMenu();
                      navigate("/live-intelligence");
                    }}
                    sx={{
                      px: "20px",
                      py: "10px",
                      borderBottom: "1px solid #f5f5f7",
                      borderLeft: `3px solid ${urgencyCfg.color}`,
                      backgroundColor: urgencyCfg.bg,
                      "&:hover": { backgroundColor: urgencyCfg.bg, opacity: 0.85 },
                      "&:last-child": { borderBottom: "none" },
                    }}
                  >
                    <ListItemText
                      primary={
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, mb: 0.3 }}>
                          <Typography
                            sx={{
                              fontFamily: mono,
                              fontSize: "8px",
                              fontWeight: 700,
                              color: urgencyCfg.color,
                              letterSpacing: "1px",
                              textTransform: "uppercase",
                              backgroundColor: urgencyCfg.color + "20",
                              px: "5px",
                              py: "1px",
                              borderRadius: "3px",
                            }}
                          >
                            {item.urgency}
                          </Typography>
                          <Typography
                            sx={{
                              fontFamily: mono,
                              fontSize: "8px",
                              color: "#64748b",
                              letterSpacing: "0.5px",
                            }}
                          >
                            {item.type}
                          </Typography>
                        </Box>
                      }
                      secondary={
                        <Typography
                          sx={{
                            fontFamily: mono,
                            fontSize: "11px",
                            fontWeight: 600,
                            color: "#1e293b",
                            lineHeight: 1.4,
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                          }}
                        >
                          {item.location?.country
                            ? `[${item.location.country}] `
                            : ""}
                          {item.description?.slice(0, 100)}
                          {item.description?.length > 100 ? "..." : ""}
                        </Typography>
                      }
                    />
                  </ListItemButton>
                );
              })}
            </List>

            {/* View intel button */}
            <Box sx={{ p: "8px", borderBottom: "1px solid #f0f1f3" }}>
              <Button
                fullWidth
                onClick={() => {
                  handleCloseMenu();
                  navigate("/live-intelligence");
                }}
                sx={{
                  fontFamily: mono,
                  fontSize: "9px",
                  fontWeight: 700,
                  letterSpacing: "1px",
                  textTransform: "uppercase",
                  color: "#dc2626",
                  borderRadius: "6px",
                  py: "7px",
                  border: "1px solid #fecaca",
                  transition: "all 0.2s ease",
                  "&:hover": { backgroundColor: "#fef2f2", borderColor: "#dc2626" },
                }}
              >
                View Live Intelligence Feed
              </Button>
            </Box>
          </>
        )}

        {/* Footer */}
        <Box
          sx={{
            borderTop: "1px solid #f0f1f3",
            p: "8px",
          }}
        >
          <Button
            fullWidth
            onClick={handleViewAll}
            sx={{
              fontFamily: mono,
              fontSize: "10px",
              fontWeight: 600,
              letterSpacing: "1px",
              textTransform: "uppercase",
              color: "#64748b",
              borderRadius: "6px",
              py: "8px",
              transition: "all 0.2s ease",
              "&:hover": {
                backgroundColor: "#f4f6f9",
                color: "#0085E5",
              },
            }}
          >
            View All Notifications
          </Button>
        </Box>
      </Menu>
    </>
  );
}

function NotificationItem({ notification, handleNotiClick }) {
  return (
    <ListItemButton
      sx={{
        px: "20px",
        py: "12px",
        borderBottom: "1px solid #f5f5f7",
        transition: "background-color 0.15s ease",
        ...(notification.isUnRead && {
          bgcolor: "#f8fbff",
          borderLeft: "3px solid #0085E5",
        }),
        ...(!notification.isUnRead && {
          borderLeft: "3px solid transparent",
        }),
        "&:hover": {
          backgroundColor: "#f4f7fa",
        },
        "&:last-child": {
          borderBottom: "none",
        },
      }}
      onClick={() => handleNotiClick(notification)}
    >
      <ListItemText
        primary={
          <Typography
            sx={{
              fontSize: "13px",
              fontWeight: 600,
              color: "#1e293b",
              lineHeight: 1.4,
            }}
          >
            {notification?.title}
          </Typography>
        }
        secondary={
          <Typography
            variant="caption"
            sx={{
              display: "flex",
              alignItems: "center",
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: "10px",
              color: "#94a3b8",
              mt: "3px",
            }}
          >
            {moment(notification?.createdAt).fromNow()}
          </Typography>
        }
      />
    </ListItemButton>
  );
}
